(ns hai.web-app.add-menu
  "Task add modal + shared note-create event.

  Mobile drives an add bottom-sheet (hai.home.mobile.add-sheet) whose task
  tile opens the modal defined here; note creation goes through the expanded
  editor (hai.notes.create) on every surface. The desktop universal
  \"Add anything…\" bar dispatches the shared `::add-note` event below when
  note text is typed straight into it."
  (:require
   [hai.notes.data :as notes-data]
   [hai.notes.mutations :as mutations]
   [hai.tasks.assignee-picker :as assignee-picker]
   [hai.tasks.data :as tasks-data]
   [hai.tasks.form :as form]
   [hai.tasks.header :as header]
   [hai.tasks.mini-app :as tasks-mini-app]
   [hai.ui.components :as c]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s >e defevent defevent-fx defsub]]
   [lambdaisland.ornament :as o]))

;; ---------------------------------------------------------------------------
;; Note events
;; ---------------------------------------------------------------------------

(defevent-fx ::add-note [_ payload]
  {:resolve (assoc (mutations/upsert-resolve
                    (mutations/upsert-params {:source :web-app} payload)
                    [:note/uuid])
                   :store-in [::add-note-result]
                   :on-success-fx [[:dispatch [::add-note-done]]])})

;; A note can now be added from anywhere, so refresh whichever list the
;; user is actually looking at — and nothing otherwise.
(defevent-fx ::add-note-done [{:keys [db]} _]
  (let [route (get-in db [:routing/current-route :data :name])]
    {:fx (into [[:dispatch [:toast/success {:text "Note saved"}]]]
               (case route
                 :timelines/view [[:dispatch [:timeline/load-entries]]]
                 :notes/view     [[:dispatch [:table/resolve notes-data/table-id]]]
                 []))}))

;; ---------------------------------------------------------------------------
;; Task events
;;
;; Task creation reuses hai.tasks.data/create, which persists via the
;; task/create resolver and prepends to :hai.tasks.data/list — the same
;; key the Desktop card subscribes to and reloads on every visit. So a task
;; added from here shows up wherever tasks are shown, no extra refresh.
;; ---------------------------------------------------------------------------

(defsub ::task-modal-open? [db]
  (get db ::task-modal-open? false))

(defevent ::open-task-modal [db _]
  (assoc db ::task-modal-open? true))

(defevent ::close-task-modal [db _]
  (dissoc db ::task-modal-open?))

;; ---------------------------------------------------------------------------
;; Task modal
;; ---------------------------------------------------------------------------

;; Radix wires the dialog's accessible name and description to its own Title
;; and Description; the visible title is the card's header bar, so both are
;; hidden here rather than rendered twice.
(def ^:private visually-hidden
  {:position "absolute" :width "1px" :height "1px" :padding "0" :margin "-1px"
   :overflow "hidden" :clip "rect(0 0 0 0)" :white-space "nowrap" :border "0"})

;; The mini-app card is the modal's surface, so the dialog panel underneath it
;; keeps only its size — no padding and no background of its own.
(def ^:private bare-dialog-panel
  {:padding "0" :background "transparent" :box-shadow "none"})

;; The duck-tape strip pins a card to the dashboard. A modal floats over the
;; page instead, so it sheds the tape and keeps the rest of the card.
(o/defstyled modal-card tasks-mini-app/card
  [:&::before {:display "none"}])

(def ^:private close-icon (icons/mui-icon :outlined :close))

(defn task-modal []
  (let [open? @(<s [::task-modal-open?])
        close! (fn [] (>e [::close-task-modal]))]
    [c/DialogRoot
     {:open open?
      :onOpenChange (fn [opening?]
                      (when-not opening?
                        (close!)))}
     [c/DialogContent {:max-width "480px" :style bare-dialog-panel}
      [c/DialogTitle {:style visually-hidden} "Add a task"]
      [c/DialogDescription {:style visually-hidden} "Create a new task."]
      ;; Mounted only while open, so each visit gets a fresh composer — the
      ;; form stages into its own atoms and has nothing to reset. No assignee is
      ;; staged: an unpicked composer means the creator, same as everywhere.
      (when open?
        [modal-card
         [header/bar {:title "Add a task"}
          [:button.bar-action-btn
           {:type "button"
            :aria-label "Cancel"
            :title "Cancel"
            :on-click close!}
           [close-icon {:size 18}]]]
         [form/task-form
          {:show-hint? true
           :on-save (fn [task] (>e [::tasks-data/create task]))
           :on-close close!
           :render-assignee-picker
           (fn [{:keys [on-pick trigger]}]
             [assignee-picker/component
              {:editable? true
               :on-pick on-pick
               :label "Assign"
               :trigger trigger}])}]])]]))

(defn modals []
  [:<>
   [task-modal]])
